import { useQuery } from "@apollo/client";
import asanaLogo from "@static/img/asana.png";
import chatGptLogo from "@static/img/chatgpt-logo.svg";
import claudeLogo from "@static/img/claude-logo.svg";
import fellowLogo from "@static/img/fellow-logo.svg";
import firefliesLogo from "@static/img/fireflies-logo.png";
import geminiLogo from "@static/img/gemini-logo.svg";
import githubLogo from "@static/img/github.png";
import googleDriveLogo from "@static/img/google-drive.svg";
import granolaLogo from "@static/img/granola-logo.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira-icon.png";
import linearLogo from "@static/img/linear.png";
import notionLogo from "@static/img/notion.svg";
import pipedriveLogo from "@static/img/pipedrive-logo.svg";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import { uniq } from "lodash";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  IntegrationTypes,
} from "types/graphql-schema";

import AccountIntegrationDetailDialog from "@apps/account/components/integrations/integration-detail-page";
import McpIntegrationDialog from "@apps/account/components/integrations/mcp-integration-dialog";
import OrgIntegrationDetailDialog from "@apps/org-settings/components/integrations/integration-detail-page";
import IntegrationCard, {
  ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP,
} from "@apps/org-settings/components/integrations/integrations-card";
import getMergeDevIntegrationsQuery from "@apps/org-settings/graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "@apps/org-settings/graphql/get-organization-integrations";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { role } from "@helpers/constants";

const fallbackIntegrationIds = [
  "slack",
  "teams",
  "github",
  "claude",
  "chatgpt",
];

const normalizeIntegrationKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const parseOnboardingData = (value: unknown) => {
  if (!value) {
    return {};
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (typeof value === "object") {
    return value;
  }
  return {};
};

type ConnectKind =
  | "account"
  | "external"
  | "fellow"
  | "merge"
  | "mcp"
  | "native-url"
  | "slack";

// Which settings modal a card should open, mirroring the Settings pages.
// "organization" -> org integration detail dialog, "account" -> personal
// integration detail dialog, "ai" -> the AI Tools (MCP) dialog. Definitions
// without a detailScope fall back to navigating to their settings page.
type DetailScope = "organization" | "account" | "ai";

type IntegrationDefinition = {
  id: string;
  name: string;
  logo: string;
  summary: string;
  connectKind: ConnectKind;
  detailScope?: DetailScope;
  // Slug expected by the target detail dialog when it differs from `id`.
  detailSlug?: string;
  getConnectUrl?: (organization: any) => string;
  isConnected: (user: any, organization: any) => boolean;
  settingsUrl: (organization: any) => string;
};

type IntegrationCardModel = IntegrationDefinition & {
  configured: boolean;
};

type OpenTarget = { scope: DetailScope; slug?: string };

const getOrganizationIntegrationsSettingsUrl = (organization: any) =>
  `/settings/organization/${organization.id}/integrations`;

const getAccountIntegrationsSettingsUrl = () => "/account/integrations";

const canManageOrganizationIntegrations = (organization: any) =>
  [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
    organization?.userMembership?.role ?? "",
  );

const isOrganizationManagedIntegration = (integration: IntegrationDefinition) =>
  integration.detailScope === "organization" ||
  ["external", "fellow", "merge", "native-url", "slack"].includes(
    integration.connectKind,
  );

const integrationDefinitions: Record<string, IntegrationDefinition> = {
  slack: {
    id: "slack",
    name: "Slack",
    logo: slackLogo,
    summary:
      "Pull messages from public Slack channels to enrich AI summaries and reviews.",
    connectKind: "slack",
    detailScope: "organization",
    isConnected: (_user, organization) =>
      Boolean(organization.hasSlackConnection),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  teams: {
    id: "teams",
    name: "Teams",
    logo: teamsLogo,
    summary:
      "Add the Topicflow bot to Microsoft Teams so employees can ask questions from Teams.",
    connectKind: "native-url",
    detailScope: "organization",
    getConnectUrl: (organization) =>
      `/sso/microsoft?request_transcript_scopes=true&request_calendar_scope=true&request_bot_scope=true&next=/msteams/subscribe_to_transcripts?org_id=${organization.id}&integration_link_to_org_id=${organization.id}`,
    isConnected: (_user, organization) =>
      Boolean(
        organization.hasTeamsInstallation ||
          organization.hasTeamsTranscriptConnection,
      ),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  github: {
    id: "github",
    name: "GitHub",
    logo: githubLogo,
    summary:
      "Preview GitHub issues and pull requests in meeting notes and enrich your work histories and coaching insights.",
    connectKind: "external",
    detailScope: "organization",
    getConnectUrl: () =>
      import.meta.env.DEV
        ? "https://github.com/apps/topicflow-dev/installations/select_target"
        : "https://github.com/apps/topicflow/installations/select_target",
    isConnected: (user, organization) =>
      Boolean(organization.hasGithubInstallation || user.hasGithubAuth),
    settingsUrl: (organization) =>
      organization.hasGithubInstallation
        ? getOrganizationIntegrationsSettingsUrl(organization)
        : getAccountIntegrationsSettingsUrl(),
  },
  claude: {
    id: "claude",
    name: "Claude",
    logo: claudeLogo,
    summary: "Connect Topicflow to Claude via the MCP server.",
    connectKind: "mcp",
    detailScope: "ai",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  chatgpt: {
    id: "chatgpt",
    name: "ChatGPT",
    logo: chatGptLogo,
    summary: "Connect Topicflow to ChatGPT via the MCP server.",
    connectKind: "mcp",
    detailScope: "ai",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  gemini: {
    id: "gemini",
    name: "Gemini",
    logo: geminiLogo,
    summary: "Connect Topicflow to Gemini via the MCP server.",
    connectKind: "mcp",
    detailScope: "ai",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  linear: {
    id: "linear",
    name: "Linear",
    logo: linearLogo,
    summary:
      "Preview Linear issues and projects in meeting notes and enrich your work histories and coaching insights.",
    connectKind: "native-url",
    detailScope: "organization",
    getConnectUrl: (organization) =>
      `/sso/linear?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: () => false,
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  jira: {
    id: "jira",
    name: "Jira",
    logo: jiraLogo,
    summary: "Preview Jira issues in meeting notes and embed them inline.",
    connectKind: "account",
    detailScope: "account",
    getConnectUrl: () => "/sso/jira?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasJiraAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  fellow: {
    id: "fellow",
    name: "Fellow",
    logo: fellowLogo,
    summary:
      "Import Fellow transcripts into matching Topicflow meetings automatically.",
    connectKind: "fellow",
    detailScope: "organization",
    isConnected: (_user, organization) =>
      Boolean(organization.hasFellowTranscriptSyncConnection),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  salesforce: {
    id: "salesforce",
    name: "Salesforce",
    logo: salesforceLogo,
    summary:
      "Preview Salesforce Opportunities in meeting notes and enrich your work histories and coaching insights.",
    connectKind: "native-url",
    detailScope: "organization",
    getConnectUrl: (organization) =>
      `/sso/salesforce?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (user) => Boolean(user.hasSalesforceAuth),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  hubspot: {
    id: "hubspot",
    name: "HubSpot",
    logo: hubspotLogo,
    summary: "Preview HubSpot contacts and deals in your meeting notes.",
    connectKind: "account",
    detailScope: "account",
    getConnectUrl: () => "/sso/hubspot?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasHubspotAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  pipedrive: {
    id: "pipedrive",
    name: "Pipedrive",
    logo: pipedriveLogo,
    summary: "Sync Pipedrive deals and contacts into Topicflow.",
    connectKind: "merge",
    detailScope: "organization",
    isConnected: () => false,
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  granola: {
    id: "granola",
    name: "Granola",
    logo: granolaLogo,
    summary: "Import Granola meeting notes and transcripts into Topicflow.",
    connectKind: "account",
    detailScope: "account",
    isConnected: (user) => Boolean(user.hasGranolaPersonalApiToken),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  fireflies: {
    id: "fireflies",
    name: "Fireflies",
    logo: firefliesLogo,
    summary: "Import Fireflies transcripts into matching Topicflow meetings.",
    connectKind: "account",
    detailScope: "account",
    isConnected: (user) => Boolean(user.hasFirefliesPersonalApiToken),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  read_ai: {
    id: "read_ai",
    name: "Read.AI",
    logo: readAiLogo,
    summary: "Import Read.AI transcripts into matching Topicflow meetings.",
    connectKind: "native-url",
    detailScope: "account",
    detailSlug: "read-ai",
    getConnectUrl: (organization) =>
      `/sso/readai?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (user) => Boolean(user.hasReadAiAuth),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  asana: {
    id: "asana",
    name: "Asana",
    logo: asanaLogo,
    summary:
      "Preview Asana tasks in meeting notes and enrich your work histories and coaching insights.",
    connectKind: "account",
    detailScope: "account",
    getConnectUrl: () => "/sso/asana?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasAsanaAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  notion: {
    id: "notion",
    name: "Notion",
    logo: notionLogo,
    summary: "Reference Notion pages and databases from Topicflow.",
    connectKind: "account",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  google_drive: {
    id: "google_drive",
    name: "Google Drive",
    logo: googleDriveLogo,
    summary:
      "Link Google Drive files and connect Google Sheets data to your KPIs.",
    connectKind: "account",
    isConnected: (user) => Boolean(user.hasGoogleSheetsAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  youtrack: {
    id: "youtrack",
    name: "YouTrack",
    logo: youtrackLogo,
    summary:
      "Sync YouTrack issues to work history and embed them in meeting notes.",
    connectKind: "native-url",
    detailScope: "organization",
    getConnectUrl: (organization) =>
      `/sso/youtrack?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (_user, organization) =>
      Boolean(organization.hasYoutrackIntegration),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
};

const orgIntegrationTypeToId: Partial<Record<IntegrationTypes, string>> = {
  [IntegrationTypes.Hubspot]: "hubspot",
  [IntegrationTypes.Jira]: "jira",
  [IntegrationTypes.Linear]: "linear",
  [IntegrationTypes.ReadAi]: "read_ai",
  [IntegrationTypes.Salesforce]: "salesforce",
  [IntegrationTypes.Teams]: "teams",
  [IntegrationTypes.Youtrack]: "youtrack",
};

const YourIntegrations = ({
  currentUser,
  currentOrganization,
}: {
  currentUser: any;
  currentOrganization: any;
}) => {
  const history = useHistory();
  const [openTarget, setOpenTarget] = useState<OpenTarget | null>(null);

  const organizationId = currentOrganization.id;
  const hasValidOrganization = organizationId && organizationId > 0;
  const canManageOrgIntegrations =
    canManageOrganizationIntegrations(currentOrganization);
  const moreIntegrationsUrl = canManageOrgIntegrations
    ? getOrganizationIntegrationsSettingsUrl(currentOrganization)
    : getAccountIntegrationsSettingsUrl();

  const { data: orgIntegrationsData } = useQuery<
    GetOrganizationIntegrationsQuery,
    GetOrganizationIntegrationsQueryVariables
  >(getOrganizationIntegrations, {
    skip: !hasValidOrganization,
    variables: { organizationId },
  });
  const { data: mergeIntegrationsData } = useQuery<
    GetMergeDevIntegrationsQuery,
    GetMergeDevIntegrationsQueryVariables
  >(getMergeDevIntegrationsQuery, {
    skip: !hasValidOrganization || !waffle.flag_is_active("merge_dev_enabled"),
    variables: { organizationId },
  });

  const onboardingIntegrationIds = useMemo<string[]>(() => {
    const onboardingData = parseOnboardingData(currentUser.onboardingData);
    const accountSetup = (onboardingData as any).account_setup;
    return Array.isArray(accountSetup?.integrationIds)
      ? accountSetup.integrationIds.filter(
          (integrationId: unknown): integrationId is string =>
            typeof integrationId === "string",
        )
      : [];
  }, [currentUser.onboardingData]);

  const connectedOrgIntegrationIds = useMemo(() => {
    const ids =
      orgIntegrationsData?.organization?.integrations.edges
        .map((edge) => edge?.node?.integrationType)
        .map((integrationType) =>
          integrationType ? orgIntegrationTypeToId[integrationType] : null,
        )
        .filter((id): id is string => Boolean(id)) || [];

    return new Set(ids);
  }, [orgIntegrationsData]);

  const connectedMergeIntegrationIds = useMemo(
    () =>
      new Set(
        (mergeIntegrationsData?.mergeDevIntegrations || [])
          .map((integration) => integration?.integrationName)
          .filter((slug): slug is string => Boolean(slug))
          .map(normalizeIntegrationKey),
      ),
    [mergeIntegrationsData],
  );

  const cards = useMemo(() => {
    const configuredCards = Object.values(integrationDefinitions)
      .map((definition): IntegrationCardModel => {
        const configured =
          definition.isConnected(currentUser, currentOrganization) ||
          connectedOrgIntegrationIds.has(definition.id) ||
          connectedMergeIntegrationIds.has(
            normalizeIntegrationKey(definition.id),
          );
        return { ...definition, configured };
      })
      .filter((card) => card.configured);

    const selectedCards = onboardingIntegrationIds
      .map((integrationId) => integrationDefinitions[integrationId])
      .filter((definition): definition is IntegrationDefinition =>
        Boolean(definition),
      )
      .map(
        (definition): IntegrationCardModel => ({
          ...definition,
          configured:
            definition.isConnected(currentUser, currentOrganization) ||
            connectedOrgIntegrationIds.has(definition.id) ||
            connectedMergeIntegrationIds.has(
              normalizeIntegrationKey(definition.id),
            ),
        }),
      );

    const fallbackCards = fallbackIntegrationIds.map(
      (integrationId): IntegrationCardModel => ({
        ...integrationDefinitions[integrationId],
        configured:
          integrationDefinitions[integrationId].isConnected(
            currentUser,
            currentOrganization,
          ) || connectedOrgIntegrationIds.has(integrationId),
      }),
    );

    const orderedIds = uniq([
      ...configuredCards.map((card) => card.id),
      ...selectedCards.map((card) => card.id),
      ...fallbackCards.map((card) => card.id),
    ]);
    const cardsById = new Map<string, IntegrationCardModel>();
    [...configuredCards, ...selectedCards, ...fallbackCards].forEach((card) => {
      if (!cardsById.has(card.id)) {
        cardsById.set(card.id, card);
      }
    });

    return orderedIds
      .map((id) => cardsById.get(id))
      .filter((card): card is IntegrationCardModel => Boolean(card))
      .slice(0, 5);
  }, [
    connectedMergeIntegrationIds,
    connectedOrgIntegrationIds,
    currentOrganization,
    currentUser,
    onboardingIntegrationIds,
  ]);

  const handleClickIntegration = (integration: IntegrationCardModel) => {
    if (integration.detailScope) {
      setOpenTarget({
        scope: integration.detailScope,
        slug: integration.detailSlug ?? integration.id,
      });
      return;
    }

    // Integrations without a matching settings modal (e.g. Notion, Google
    // Drive) fall back to navigating to their settings page.
    history.push(integration.settingsUrl(currentOrganization));
  };

  const closeDialog = () => setOpenTarget(null);

  if (!hasValidOrganization || !cards.length) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold leading-6 text-gray-900">
        Your Integrations
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((integration) => {
          const isDisabled =
            !canManageOrgIntegrations &&
            isOrganizationManagedIntegration(integration);
          return (
            <IntegrationCard
              key={integration.id}
              name={integration.name}
              logo={integration.logo}
              summary={integration.summary}
              statusLabel={
                isDisabled && !integration.configured
                  ? "Admin only"
                  : integration.configured
                    ? "Enabled"
                    : "Connect"
              }
              statusTone={
                isDisabled && !integration.configured
                  ? "warning"
                  : integration.configured
                    ? "enabled"
                    : "neutral"
              }
              isDisabled={isDisabled}
              disabledTooltip={
                isDisabled ? ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP : undefined
              }
              onClick={() => handleClickIntegration(integration)}
            />
          );
        })}
        <AppLink
          to={moreIntegrationsUrl}
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-4 text-center hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100">
            <TopicflowIcon type="viewGrid" className="h-4 w-4 text-gray-900" />
          </div>
          <div className="text-sm font-semibold text-gray-900">
            Connect more integrations
          </div>
        </AppLink>
      </div>

      <McpIntegrationDialog
        open={openTarget?.scope === "ai"}
        onClose={closeDialog}
      />

      <OrgIntegrationDetailDialog
        organization={currentOrganization}
        slug={
          openTarget?.scope === "organization" ? (openTarget.slug ?? "") : ""
        }
        open={openTarget?.scope === "organization"}
        onClose={closeDialog}
      />

      <AccountIntegrationDetailDialog
        account={currentUser}
        slug={openTarget?.scope === "account" ? (openTarget.slug ?? "") : ""}
        open={openTarget?.scope === "account"}
        onClose={closeDialog}
      />
    </section>
  );
};

export default YourIntegrations;
